import {
  Drawer,
  DrawerContent,
  Tabs,
  Tab,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '@/components/ui';
import { useDrawer } from '@/store/drawer';
import { useDrawerActions } from '@/store/drawer';
import { CommentsContent } from '@/components/people/people/comments';
import { useModalSize } from '@/hooks/screen';
import ProfileAvatarCard from '../people/people/profileAvatarCard';
import { Milestones } from './milestones';
import { Details } from './details';
import { StandardIcon } from '../common/icon';
import { useProfileModal, useTagsModal } from '../profile';
import type { Person } from '@/store/people/types';
import type { PersonFieldKey } from '../people/people/fields';

type ActionKey = 'edit' | 'tags' | 'archive';
type Action = { label: string; key: ActionKey; icon: string };

const Actions: Action[] = [
  {
    key: 'edit',
    label: 'Edit Details',
    icon: 'mdi:pencil-outline',
  },
  {
    key: 'tags',
    label: 'Edit Tags',
    icon: 'mdi:tag-outline',
  },
  {
    key: 'archive',
    label: 'Archive',
    icon: 'mdi:package-down',
  },
];

const Fields: PersonFieldKey[] = [
  'firstname',
  'preferredname',
  'lastname',
  'birthdate',
  'gender',
  'marital_status',
  'email',
  'phone',
  'address',
  'member_status',
  'campus_id',
  'laygroup1',
  'laygroup2',
];

export function PersonDrawer() {
  const { state, access } = useDrawer();
  const drawerSize = useModalSize();
  const {
    setOpen,
    setCurrentTab,
    setCurrentPerson,
    setCurrentComment,
    setCurrentMention,
  } = useDrawerActions();
  const {
    setOpen: setModalIsOpen,
    setCurrentPerson: setModalPerson,
    setOnUpdated,
    setFields,
  } = useProfileModal();
  const {
    setOpen: setTagsModalOpen,
    setCurrentPerson: setTagsModalPerson,
    setOnUpdated: setTagsOnUpdated,
  } = useTagsModal();

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setCurrentPerson(null);
      setCurrentComment(null);
      setCurrentMention(null);
      setCurrentTab('profile');
    }
  };

  const onAction = (key: ActionKey) => {
    switch (key) {
      case 'edit':
        editHandler();
        break;
      case 'tags':
        tagsHandler();
        break;
      case 'archive':
        break;
    }
  };

  const onUpdated = (person: Person) => {
    const mergedPerson =
      state.currentPerson?.id === person.id
        ? {
            ...state.currentPerson,
            data: {
              ...(state.currentPerson.data || {}),
              ...(person.data || {}),
            },
          }
        : person;

    setCurrentPerson(mergedPerson);
    setModalIsOpen(false);
    setModalPerson(null);
  };

  const editHandler = () => {
    setModalPerson(state.currentPerson);
    setOnUpdated({ callback: onUpdated });
    setFields(Fields);
    setModalIsOpen(true);
  };

  const tagsHandler = () => {
    setTagsModalPerson(state.currentPerson);
    setTagsOnUpdated({ callback: onUpdated });
    setTagsModalOpen(true);
  };

  const commentsAccess =
    access.access.comment_view === 'allow' ||
    access.access.comment_create === 'allow';

  const editAccess = access.access.people_edit === 'allow';
  const archiveAccess = access.access.people_archive === 'allow';

  const actions = Actions.filter((item) => {
    if ((item.key === 'edit' || item.key === 'tags') && !editAccess) {
      return false;
    }

    if (item.key === 'archive' && !archiveAccess) {
      return false;
    }

    return true;
  });

  return (
    <Drawer
      isOpen={state.open}
      onOpenChange={handleOpenChange}
      placement='right'
      isDismissable={false}
      className='overflow-hidden'
      size={drawerSize}
      hideCloseButton
    >
      <DrawerContent className='overflow-y-auto overflow-x-hidden bg-[#F0F2F5] dark:bg-slate-900'>
        {!state.currentPerson ? (
          <></>
        ) : (
          <div className='min-h-full px-4 pb-4'>
            <div className='relative mb-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800'>
              <div className='flex flex-row'>
                <div className='relative flex w-full flex-row pb-2'>
                  <ProfileAvatarCard
                    profileData={state.currentPerson.data}
                    isDrawer
                  />
                  {actions.length > 0 && (
                    <div className='-mb-2 flex flex-row justify-end self-end'>
                      <Dropdown>
                        <DropdownTrigger>
                          <Button isIconOnly variant='light'>
                            <StandardIcon id='mdi:dots-vertical' />
                          </Button>
                        </DropdownTrigger>
                        <DropdownMenu items={actions}>
                          {(item) => (
                            <DropdownItem
                              key={item.key}
                              onPress={() => onAction(item.key)}
                            >
                              <div className='flex flex-row items-center gap-2'>
                                <StandardIcon id={item.icon} />
                                <span className='flex-grow'>{item.label}</span>
                              </div>
                            </DropdownItem>
                          )}
                        </DropdownMenu>
                      </Dropdown>
                    </div>
                  )}
                </div>
                <Button
                  size='sm'
                  variant='light'
                  isIconOnly
                  className='absolute right-2 top-3'
                  onPress={() => handleOpenChange(false)}
                >
                  <StandardIcon id='mdi:close' className='!text-default-500' />
                </Button>
              </div>
            </div>
            <Tabs
              selectedKey={state.currentTab}
              onSelectionChange={(key) =>
                setCurrentTab(key as 'profile' | 'comments' | 'milestones')
              }
            >
              <Tab key='profile' title='Details'>
                <Details person={state.currentPerson} />
              </Tab>
              {commentsAccess && (
                <Tab key='comments' title='Comments'>
                  <CommentsContent
                    subject={state.currentPerson}
                    access={access.access}
                    target={state.currentComment}
                    currentMention={state.currentMention || undefined}
                  />
                </Tab>
              )}
              <Tab key='milestones' title='Milestones'>
                <Milestones
                  person={state.currentPerson}
                  access={access.access}
                />
              </Tab>
            </Tabs>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
}
